// 2026-11-05 (feedback pipeline): the Director's draft report or idea, on screen until the user decides (Keaton: the
// Director sometimes sent reports he didn't want). One card for the whole app (DraftHost in App), filled by the
// `feedback_draft` directive (src/drafts.ts). Send posts it (with their edit), Edit opens the words in place, Discard
// deletes it; each tells the Director what they did, so it never says "filed" before they've sent it.
import { useEffect, useState, useSyncExternalStore } from "react";
import { director } from "../director";
import { closeDraft, currentDraft, patchDraft, subscribeDrafts } from "../drafts";
import { discardDraftNow, sendDraftNow } from "../pipeline";
import { toast } from "../store";
import { Icon } from "./bits";

export function DraftHost() {
  const d = useSyncExternalStore(subscribeDrafts, currentDraft, currentDraft);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  useEffect(() => {
    setEditing(false);
    setText(d?.text ?? "");
  }, [d?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // a sent card thanks them, then goes
  useEffect(() => {
    if (d?.state !== "sent") return;
    const t = setTimeout(closeDraft, 4000);
    return () => clearTimeout(t);
  }, [d?.state, d?.id]);
  if (!d) return null;
  const problem = d.kind === "problem";
  const busy = d.state === "sending";

  const send = async () => {
    const edited = editing && text.trim() !== d.text ? text.trim() : null;
    if (editing && !text.trim()) return patchDraft(d.id, { error: "Say it in a few words first." });
    patchDraft(d.id, { state: "sending", error: null });
    try {
      const r = await sendDraftNow(d.id, edited);
      patchDraft(d.id, { state: "sent", text: r.text ?? d.text });
      setEditing(false);
      director.context(`The user tapped Send on the ${problem ? "problem report" : "note to the team"} you drafted${edited ? ", after changing the words" : ""}. It's sent now; don't send it again.`);
    } catch (e) {
      patchDraft(d.id, { state: "draft", error: (e as Error).message || "Couldn't send it. Try again in a moment." });
    }
  };
  const discard = async () => {
    try {
      await discardDraftNow(d.id);
    } catch {
      /* already gone: the card goes either way */
    }
    closeDraft();
    director.context(`The user discarded the ${problem ? "problem report" : "note"} you drafted. Nothing was sent; don't bring it up again unless they do.`);
  };

  return (
    <aside className={`draft-card${d.state === "sent" ? " is-sent" : ""}`} data-no-shot aria-live="polite" aria-label={problem ? "Draft problem report" : "Draft note to the team"}>
      <header className="draft-head">
        <Icon name={problem ? "bug" : "chat"} size={16} />
        <strong>{d.state === "sent" ? "Sent. Thanks." : problem ? "Report this problem?" : "Send this to the team?"}</strong>
        {d.state !== "sent" && (
          <button type="button" className="icon-btn draft-x" onClick={closeDraft} aria-label="Hide the draft (it stays a draft)">
            <Icon name="close" size={16} />
          </button>
        )}
      </header>
      {editing ? (
        <textarea className="draft-text-edit" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} aria-label="The report" autoFocus />
      ) : (
        <p className="draft-text">{d.text}</p>
      )}
      {d.state !== "sent" && <p className="draft-note muted">{problem ? "Not sent yet. Support gets this with your latest jobs." : "Not sent yet."}</p>}
      {d.error && <p className="draft-error">{d.error}</p>}
      {d.state !== "sent" && (
        <div className="draft-acts">
          <button type="button" className="btn btn-primary btn-small" onClick={() => void send()} disabled={busy}>
            {busy ? "Sending" : "Send"}
          </button>
          {!editing && (
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setEditing(true)} disabled={busy}>
              Edit
            </button>
          )}
          <button type="button" className="btn btn-quiet btn-small" onClick={() => void discard().catch(() => toast("Couldn't discard it.", "bad"))} disabled={busy}>
            Discard
          </button>
        </div>
      )}
    </aside>
  );
}
